import React, { useState } from 'react';
import { ArrowLeft, Save, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';

const TowerSiteForm = ({ onBack, siteData = null, mode = 'add' }) => {
  const [formData, setFormData] = useState({
    siteId: siteData?.siteId || '',
    siteName: siteData?.siteName || '',
    projectName: siteData?.projectName || '',
    regional: siteData?.regional || 'JAWA TIMUR',
    island: siteData?.island || 'JAWA',
    longitude: siteData?.longitude || '',
    latitude: siteData?.latitude || '',
    kelurahan: siteData?.kelurahan || '',
    kecamatan: siteData?.kecamatan || '',
    kabupaten: siteData?.kabupaten || '',
    provinsi: siteData?.provinsi || '',
    address: siteData?.address || '',
    landrentEndDate: siteData?.landrentEndDate || '',
    towerHeight: siteData?.towerHeight || '',
    typeSiteGF: siteData?.typeSiteGF || 'GF',
    typeSiteContract: siteData?.typeSiteContract || 'MACRO',
    typeOfTower: siteData?.typeOfTower || '',
    sow: siteData?.sow || '',
    siteIdOperator: siteData?.siteIdOperator || '',
    siteNameOperator: siteData?.siteNameOperator || ''
  });

  const [expandedSection, setExpandedSection] = useState('basic');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.siteId.trim()) newErrors.siteId = 'Site ID is required';
    if (!formData.siteName.trim()) newErrors.siteName = 'Site Name is required';
    if (!formData.projectName.trim()) newErrors.projectName = 'Project Name is required';
    if (!formData.towerHeight) newErrors.towerHeight = 'Tower Height is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert(`Tower site ${mode === 'add' ? 'created' : 'updated'} successfully!`);
      if (onBack) onBack();
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      siteId: '',
      siteName: '',
      projectName: '',
      regional: 'JAWA TIMUR',
      island: 'JAWA',
      longitude: '',
      latitude: '',
      kelurahan: '',
      kecamatan: '',
      kabupaten: '',
      provinsi: '',
      address: '',
      landrentEndDate: '',
      towerHeight: '',
      typeSiteGF: 'GF',
      typeSiteContract: 'MACRO',
      typeOfTower: '',
      sow: '',
      siteIdOperator: '',
      siteNameOperator: ''
    });
    setErrors({});
    setExpandedSection('basic');
  };

  const Section = ({ title, name, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => toggleSection(name)}
        className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${
          expandedSection === name 
            ? 'bg-gradient-to-r from-blue-50 to-red-50' 
            : 'bg-gray-50 hover:bg-gray-100'
        }`}
      >
        <h3 className={`text-lg font-bold ${expandedSection === name ? 'text-red-600' : 'text-gray-800'}`}>
          {title}
        </h3>
        {expandedSection === name ? (
          <ChevronUp className="w-5 h-5 text-red-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {expandedSection === name && (
        <div className="p-6 bg-white">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {mode === 'add' ? 'Create New Tower Site' : 'Edit Tower Site'}
              </h1>
              <p className="text-gray-600 mt-1">Fill in all required information</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="space-y-4">
              <Section title="Basic Information" name="basic">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site ID <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="siteId"
                      value={formData.siteId}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.siteId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., ETJI0219"
                    />
                    {errors.siteId && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.siteId}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="siteName"
                      value={formData.siteName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.siteName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., WIDODAREN"
                    />
                    {errors.siteName && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.siteName}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Project Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="projectName"
                      value={formData.projectName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.projectName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., Telkomsel East Java Expansion 2025"
                    />
                    {errors.projectName && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.projectName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Regional</label>
                    <select
                      name="regional"
                      value={formData.regional}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    >
                      <option value="JAWA TIMUR">JAWA TIMUR</option>
                      <option value="JAWA TENGAH">JAWA TENGAH</option>
                      <option value="JAWA BARAT">JAWA BARAT</option>
                      <option value="LAMPUNG">LAMPUNG</option>
                      <option value="SUMATERA">SUMATERA</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Island</label>
                    <select
                      name="island"
                      value={formData.island}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    >
                      <option value="JAWA">JAWA</option>
                      <option value="SUMATERA">SUMATERA</option>
                      <option value="KALIMANTAN">KALIMANTAN</option>
                      <option value="SULAWESI">SULAWESI</option>
                      <option value="BALI">BALI</option>
                    </select>
                  </div>
                </div>
              </Section>

              <Section title="Location Information" name="location">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Kelurahan</label>
                    <input
                      type="text"
                      name="kelurahan"
                      value={formData.kelurahan}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., Karangbanyu"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Kecamatan</label>
                    <input
                      type="text"
                      name="kecamatan"
                      value={formData.kecamatan}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., Widodaren"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Kabupaten</label>
                    <input
                      type="text"
                      name="kabupaten"
                      value={formData.kabupaten}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., Ngawi"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Provinsi</label>
                    <input
                      type="text"
                      name="provinsi"
                      value={formData.provinsi}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., Jawa Timur"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                      placeholder="Full address..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., 111.25138"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., -7.37549"
                    />
                  </div>
                </div>
              </Section>

              <Section title="Technical Information" name="technical">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Tower + Building Height (m) <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      name="towerHeight"
                      value={formData.towerHeight}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.towerHeight ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., 52"
                      min="1"
                    />
                    {errors.towerHeight && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.towerHeight}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type Site (GF/RT)</label>
                    <select
                      name="typeSiteGF"
                      value={formData.typeSiteGF}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    >
                      <option value="GF">GF (Green Field)</option>
                      <option value="RT">RT (Roof Top)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type Site (Contract)</label>
                    <select
                      name="typeSiteContract"
                      value={formData.typeSiteContract}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    >
                      <option value="MACRO">MACRO</option>
                      <option value="Macro">Macro</option>
                      <option value="MICRO">MICRO</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type of Tower</label>
                    <input
                      type="text"
                      name="typeOfTower"
                      value={formData.typeOfTower}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., SST, Angular 4 Legs"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">SoW</label>
                    <select
                      name="sow"
                      value={formData.sow}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select SoW</option>
                      <option value="B2S">B2S</option>
                      <option value="Collocation">Collocation</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Landrent End Date</label>
                    <input
                      type="date"
                      name="landrentEndDate"
                      value={formData.landrentEndDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </Section>

              <Section title="Operator Information" name="operator">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Site ID Operator</label>
                    <input
                      type="text"
                      name="siteIdOperator"
                      value={formData.siteIdOperator}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., ZMDU_0388"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Site Name Operator</label>
                    <input
                      type="text"
                      name="siteNameOperator"
                      value={formData.siteNameOperator}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., WIDODAREN"
                    />
                  </div>
                </div>
              </Section>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-blue-600 via-red-600 to-blue-600"></div>

          {/* Form Actions */}
          <div className="p-6 bg-gray-50">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onBack}
                className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <Save className="w-5 h-5" />
                {isSubmitting ? 'Saving...' : (mode === 'add' ? 'Create Tower Site' : 'Update Tower Site')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TowerSiteForm;